import * as React from 'react';
import * as _ from 'underscore';

import { MetricCardWithTimeSeries } from '@tableau/metrics-shared-widgets-dom/lib/src/MetricCardWithTimeSeries';
import { Messages } from '../../codegen/Localize';
import { FavoriteToggle } from '../../common/react/components/FavoriteToggle/FavoriteToggle';
import { imgPath } from '../../common/utilities/ImgUrl';
import { getMetricMeasure, metricCardProps } from '..';
import { L10n } from '../../ts/L10n/L10n';
import { IMetricDataInfo, isMetricInfo } from '../models/IMetricInfo';
import { IExplorableItemTypeInfo } from '../../ts/models/resourceInfoParts';
import { ConnectedMetricTooltip } from './MetricTooltip';
import { TooltipAnchor } from '../../app/spaces/Tooltips/TooltipAnchor';
import { CardViewStyles } from '../../app/spaces/CardView/CardViewStyles';
import { ContentCard, ContentCardProps, getContentUrlForItem } from '../../app/spaces/CardView/ContentCard';
import { DetailsTooltipStyles } from '../../app/spaces/CardView/DetailsTooltipStyles';
import { Thumbnail } from '../../app/spaces/CardView/Thumbnail';
import { doesTooltipNotHaveOwnerToDisplay } from '../../app/spaces/Tooltips/Elements/Tooltip';

interface MetricCardState {
  readonly metricData?: IMetricDataInfo;
}

export interface MetricCardOwnProps extends ContentCardProps {
}

export interface MetricCardStateProps {
  readonly metricData: IMetricDataInfo;
}

export type MetricCardProps = MetricCardOwnProps & MetricCardStateProps;

export const MetricDefaultImageUrl = imgPath('Spaces_Empty_Metrics.svg');

export class MetricCard extends ContentCard<MetricCardProps, MetricCardState> {
  public state: MetricCardState = {};

  private renderTimerHandle?: ReturnType<typeof setTimeout>;

  public render() {
    const showLastAccessedAt = this.props.inRecentsSpace || this.props.showLastAccessedAt;
    const disableAutoFocus = doesTooltipNotHaveOwnerToDisplay(this.props.content);

    return (
      <div className={CardViewStyles.Card}>
        <TooltipAnchor
          tooltipContent={this.renderTooltipContent}
          width={DetailsTooltipStyles.Width}
          onShowTooltip={this.handleShowTooltip}
          title={Messages.metricDetails()}
          disableAutoFocus={disableAutoFocus}
          disableTooltip={this.props.disableTooltip}
        >
          {this.renderMetricCard()}
        </TooltipAnchor>
        <div className={CardViewStyles.ActionRow}>
          <span className={CardViewStyles.ActionRowName} title={this.props.content.name}>
            {L10n.elideIfLong(this.props.content.name)}
          </span>
          {this.showFavoritesStar() && <span>
            <FavoriteToggle isTabbable={true}
              isFavorite={this.props.content.favorite}
              onAddFavorite={() => this.props.addContentFavorite({ ...this.props.content, contentType: 'metric'})}
              onRemoveFavorite={() => this.props.removeContentFavorite({ ...this.props.content, contentType: 'metric'})} />
          </span>}
          { this.renderActionsMenu() }
        </div>
        {
        showLastAccessedAt ? (
          <div className={CardViewStyles.CardLastAccessedAt}>
            {this.getLastAccessedAt()}
          </div>
        ) : (
          <div className={CardViewStyles.CardDescription}>
            {this.getDescription()}
          </div>
        )
        }
      </div>
    );
  }

  private renderTooltipContent = () => {
    const timestamp = this.state.metricData ? metricCardProps(this.state.metricData, isMetricInfo(this.props.content) && this.props.content).lastUpdatedTimestamp : undefined;
    return <ConnectedMetricTooltip content={this.props.content}
                                   urls={this.props.urls}
                                   lastUpdatedTimestamp={timestamp}
                                   inProjectSpace={this.props.inProjectSpace}/>;
  }

  public componentDidUpdate(): void {
    this.delayAddingDataToState(this.state, this.props.content);
  }

  public componentDidMount(): void {
    this.delayAddingDataToState(this.state, this.props.content);
  }

  public componentWillUnmount() {
    if (this.renderTimerHandle) {
      clearTimeout(this.renderTimerHandle);
    }
  }

  private delayAddingDataToState(state: MetricCardState, content: IExplorableItemTypeInfo): void {
    if (isMetricInfo(content) && this.props.metricData !== state.metricData) {
    this.renderTimerHandle = setTimeout(() => this.setState({metricData: this.props.metricData}),
      Math.floor(Math.random() * 10));
    }
  }

  private renderMetricCard() {
    if (this.haveMetricDataPoints()) {
      return renderMetricCardContent(this.state.metricData, this.props);
    } else {
      return (
        <Thumbnail
          defaultImageUrl={MetricDefaultImageUrl}
          hoveredOrSelected={this.props.hoveredOrSelected}
          navigateUrl={getContentUrlForItem(this.props.content, this.props.urls)}
          useBase64EncodedImage={true}
          allowDragAndDrop={this.props.allowDragAndDrop}
        />
      );
    }
  }


  private haveMetricDataPoints(): boolean {
    return isMetricInfo(this.props.content) && this.state.metricData && !_.isUndefined(this.state.metricData.dataPoints);
  }
}

// This is a separate function so other card components can reuse the metric thumbnail
export function renderMetricCardContent(metricData: IMetricDataInfo, contentCardProps: ContentCardProps): JSX.Element {
  const containerClasses = contentCardProps.hoveredOrSelected ? [CardViewStyles.ThumbnailPreloaded, CardViewStyles.CardHighlight]
    : [CardViewStyles.ThumbnailPreloaded];

  let metricCardProperties = metricCardProps(metricData, isMetricInfo(contentCardProps.content) && contentCardProps.content);
  if (contentCardProps.forDragPreview) {
    // when rendering the card just for a drag preview, don't show the sparkline because it makes the preview slower
    metricCardProperties.data = getMetricMeasure(metricData);
  }

  return (
    <div className={containerClasses.join(' ')} >
      <div className={CardViewStyles.SelectOverlay}></div>
      <a
        href={getContentUrlForItem(contentCardProps.content, contentCardProps.urls)}
        style={{ textDecoration: 'none' }}
        /**
         * If card reordering allowed, disable link dragging.
         * Otherwise, set `draggable` explicitly to undefined rather than true. 
         * (When `draggable` is true, the browser-provided drag avatar looks too similar 
         * to the reorder card feature's avatar, which may confuse users.)
         */
        draggable={contentCardProps.allowDragAndDrop ? false : undefined}
      >
        {<MetricCardWithTimeSeries {...metricCardProperties} />}
      </a>
    </div>
  );
}
